import { motion, AnimatePresence, animate, useMotionValue } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PassPreview from '../../generate/components/PassPreview';
import LandingLegalFooter from '../components/LandingLegalFooter';

const screenshots = [
    {
        src: '/preview1.png',
        alt: 'LoyalPass analytics dashboard',
    },
    {
        src: '/preview6.png',
        alt: 'Customer management dashboard',
    },
    {
        src: '/preview5.png',
        alt: 'Rewards management dashboard',
    },
    {
        src: '/preview4.png',
        alt: 'LoyalPass analytics overview',
    },
    {
        src: '/preview3.png',
        alt: 'Rewards management dashboard',
    },
    {
        src: '/preview2.png',
        alt: 'LoyalPass analytics overview',
    },
];

function useAnimatedCounter(target, duration = 1.2) {
    const mv = useMotionValue(0);
    const [value, setValue] = useState(target);

    useEffect(() => {
        const controls = animate(mv, target, {
            duration,
            ease: 'easeOut',
            onUpdate: (latest) => setValue(Math.round(latest)),
        });

        return () => controls.stop();
    }, [target, duration, mv]);

    return value;
}

function useHeroParticles(count = 18) {
    return useMemo(() => {
        return Array.from({ length: count }).map((_, i) => ({
            id: i,
            left: Math.random() * 100, // percent
            top: Math.random() * 60, // percent (top half mostly)
            size: 2 + Math.random() * 3, // px
            delay: Math.random() * 2,
            duration: 3 + Math.random() * 4,
            opacity: 0.15 + Math.random() * 0.25,
        }));
    }, [count]);
}

const reveal = {
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.7, ease: 'easeOut' },
};

export default function Home() {
    const [index, setIndex] = useState(0);
    const [pointsTarget, setPointsTarget] = useState(75);
    const [visitsTarget, setVisitsTarget] = useState(8);
    const particles = useHeroParticles(22);

    const points = useAnimatedCounter(pointsTarget, 1.3);
    const visits = useAnimatedCounter(visitsTarget, 1.0);

    useEffect(() => {
        const interval = setInterval(() => {
            setPointsTarget((p) => {
                const next = p + 25;
                return next > 120 ? 75 : next; // loop nicely
            });

            setVisitsTarget((v) => {
                const next = v + 1;
                return next > 12 ? 8 : next; // loop nicely
            });
        }, 3500);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setIndex((prev) => (prev + 1) % screenshots.length);
        }, 3500); // rotate every 3.5s

        return () => clearInterval(interval);
    }, []);

    return (
        <div className="min-h-screen bg-[#0D0221] text-white font-sans">
            {/* Navbar */}
            <nav className="flex justify-between items-center px-8 py-6">
                <div className="flex items-center space-x-3">
                    <img src="test.png" alt="LoyalPass Logo" className="h-10 w-auto" />
                    <span className="text-2xl font-bold text-[#00FFD0]">
                        Loyal<span className="text-[#FF4DA6]">Pass</span>
                    </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                    <Link
                        to="/register"
                        className="bg-[#00FFD0] text-[#0D0221] px-4 py-2 rounded-full font-semibold hover:bg-[#00e6bc] transition"
                    >
                        Start Free Trial
                    </Link>
                    <Link
                        to="/login"
                        className="text-gray-300 hover:text-white px-3 py-2 transition"
                    >
                        Login
                    </Link>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative overflow-hidden text-center px-6 py-20 bg-gradient-to-r from-[#0D0221] via-[#150336] to-[#0D0221] animate-gradient-x bg-[length:400%_400%]">
                {/* City background layer */}
                <div className="pointer-events-none absolute inset-0">
                    {/* soft glow / vignette */}
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />

                    {/* pulsing neon glows */}
                    <motion.div
                        className="absolute -top-24 left-1/2 h-72 w-[700px] -translate-x-1/2 rounded-full blur-3xl bg-[#00FFD0]/10"
                        animate={{ opacity: [0.08, 0.16, 0.08] }}
                        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div
                        className="absolute -top-10 left-1/3 h-64 w-[520px] -translate-x-1/2 rounded-full blur-3xl bg-[#FF4DA6]/10"
                        animate={{ opacity: [0.07, 0.14, 0.07] }}
                        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                    />

                    {/* particles (stars / city sparkle) */}
                    {particles.map((p) => (
                        <motion.span
                            key={p.id}
                            className="absolute rounded-full bg-white"
                            style={{
                                left: `${p.left}%`,
                                top: `${p.top}%`,
                                width: `${p.size}px`,
                                height: `${p.size}px`,
                                opacity: p.opacity,
                            }}
                            animate={{
                                y: [0, -10, 0],
                                opacity: [p.opacity, p.opacity + 0.2, p.opacity],
                            }}
                            transition={{
                                duration: p.duration,
                                repeat: Infinity,
                                delay: p.delay,
                                ease: 'easeInOut',
                            }}
                        />
                    ))}

                    {/* animated "city lights" band (THIS WILL SHOW) */}
                    <motion.div
                        className="absolute bottom-[72px] left-0 right-0 opacity-40"
                        initial={{ opacity: 0.18 }}
                        animate={{ opacity: [0.18, 0.42, 0.18] }}
                        transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
                    >
                        <motion.div
                            className="mx-auto h-10 w-[85%] rounded-full"
                            animate={{ backgroundPositionX: ['0px', '240px'] }}
                            transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
                            style={{
                                backgroundImage:
                                    'radial-gradient(circle, rgba(0,255,208,0.50) 1px, transparent 1px)',
                                backgroundSize: '22px 22px',
                            }}
                        />
                    </motion.div>

                    {/* skyline — faster + smooth constant direction */}
                    <motion.div
                        className="absolute bottom-0 left-0 right-0 opacity-50"
                        aria-hidden="true"
                        initial={{ x: 0 }}
                        animate={{ x: [-120, 0] }}
                        transition={{ duration: 10, ease: 'linear', repeat: Infinity }}
                    >
                        <svg viewBox="0 0 1200 220" className="w-[160%] md:w-[140%] h-auto">
                            <path
                                d="M0,220 L0,150 L30,150 L30,120 L55,120 L55,150 L80,150 L80,90 L120,90 L120,150
             L160,150 L160,60 L205,60 L205,150 L250,150 L250,110 L280,110 L280,150
             L320,150 L320,80 L360,80 L360,150 L410,150 L410,40 L460,40 L460,150
             L520,150 L520,95 L560,95 L560,150 L610,150 L610,70 L655,70 L655,150
             L700,150 L700,100 L735,100 L735,150 L780,150 L780,55 L840,55 L840,150
             L900,150 L900,85 L940,85 L940,150 L1000,150 L1000,65 L1045,65 L1045,150
             L1100,150 L1100,105 L1135,105 L1135,150 L1200,150 L1200,220 Z"
                                fill="grey"
                            />
                        </svg>
                    </motion.div>

                    {/* foreground fade so text stays readable */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0D0221] via-transparent to-transparent" />
                </div>

                {/* HERO CONTENT */}
                <motion.div
                    className="relative z-10"
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                >
                    <motion.div
                        className="inline-flex items-center rounded-full bg-indigo-100 px-4 py-1 text-sm font-medium text-indigo-800 mb-6"
                        initial={{ scale: 0.96, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
                    >
                        {/* your stars */}
                        {/* ... keep your SVGs exactly as you have them ... */}
                        {/* Trusted by 500+ businesses */}
                    </motion.div>

                    <motion.h1
                        className="text-4xl md:text-6xl font-bold text-[#FF4DA6]"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
                    >
                        Miami's Best
                    </motion.h1>

                    <motion.h1
                        className="text-4xl md:text-6xl font-bold text-white"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.28, ease: 'easeOut' }}
                    >
                        Customer Loyalty Program
                    </motion.h1>

                    <br />

                    <motion.h1
                        className="text-4xl md:text-4xl font-bold text-[#00FFD0]"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.36, ease: 'easeOut' }}
                    >
                        Designed for Small Businesses
                    </motion.h1>

                    <motion.p
                        className="mt-4 text-lg md:text-xl text-gray-300 max-w-2xl mx-auto"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.45, ease: 'easeOut' }}
                    >
                        Transform your customer relationships with a powerful, customizable loyalty
                        program that drives repeat business and boosts your revenue.
                    </motion.p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                        <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.98 }}>
                            <Link
                                to="/register"
                                className="inline-block bg-[#00FFD0] text-[#0D0221] px-8 py-3 rounded-full font-semibold hover:bg-[#00e6bc] transition shadow-lg shadow-[#00FFD0]/20"
                            >
                                Start Free Trial
                            </Link>
                        </motion.div>
                        <motion.a
                            href="#contact"
                            className="inline-block border border-white/30 text-white px-8 py-3 rounded-full font-semibold hover:border-[#FF4DA6] hover:text-[#FF4DA6] transition"
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            Book a Demo
                        </motion.a>
                    </div>

                    <motion.div
                        className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-400"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                    >
                        <span className="inline-flex items-center gap-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                                className="h-5 w-5 text-green-500"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                />
                            </svg>
                            Launch in minutes — no app required
                        </span>
                        <span className="inline-flex items-center gap-2">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                                className="h-5 w-5 text-green-500"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                                />
                            </svg>
                            No credit card required
                        </span>
                    </motion.div>
                </motion.div>
            </header>
            {/* Why Us Preview */}
            <motion.section
                {...reveal}
                className="bg-gradient-to-b from-white to-indigo-50 py-24 sm:py-32"
            >
                <div className="mx-auto max-w-7xl px-6 lg:px-8">
                    <div className="mx-auto max-w-2xl lg:text-center">
                        <h2 className="text-base font-semibold leading-7 text-indigo-600">
                            Why Choose LoyalPass
                        </h2>
                        <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Everything you need to build customer loyalty
                        </p>
                        <p className="mt-6 text-lg leading-8 text-gray-600">
                            Our platform provides all the tools you need to create, manage, and grow
                            a successful loyalty program that drives real business results.
                        </p>
                    </div>
                    <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                        <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                            <div className="flex flex-col p-6 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-x-3">
                                    <div className="rounded-lg bg-indigo-50 p-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            aria-hidden="true"
                                            data-slot="icon"
                                            className="h-6 w-6 text-indigo-600"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
                                            ></path>
                                        </svg>
                                    </div>
                                    <dt className="text-lg font-semibold leading-7 text-gray-900">
                                        Boost Repeat Purchases
                                    </dt>
                                </div>
                                <dd className="mt-4 flex flex-auto flex-col">
                                    <p className="flex-auto text-base leading-7 text-gray-600">
                                        Encourage customers to come back with points-based rewards; visit and punch-card
                                        reward paths are on the roadmap.
                                    </p>
                                    {/*<div className="mt-4 text-sm font-medium text-indigo-600">*/}
                                    {/*    Up to 40% increase*/}
                                    {/*</div>*/}
                                </dd>
                            </div>
                            <div className="flex flex-col p-6 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-x-3">
                                    <div className="rounded-lg bg-indigo-50 p-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            aria-hidden="true"
                                            data-slot="icon"
                                            className="h-6 w-6 text-indigo-600"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"
                                            ></path>
                                        </svg>
                                    </div>
                                    <dt className="text-lg font-semibold leading-7 text-gray-900">
                                        Smart Automation
                                    </dt>
                                </div>
                                <dd className="mt-4 flex flex-auto flex-col">
                                    <p className="flex-auto text-base leading-7 text-gray-600">
                                        Save time with automated rewards and personalized offers
                                        that run on autopilot.
                                    </p>
                                    {/* <div className="mt-4 text-sm font-medium text-indigo-600">Up
                                        to 90% increase
                                    </div> */}
                                </dd>
                            </div>
                            <div className="flex flex-col p-6 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-x-3">
                                    <div className="rounded-lg bg-indigo-50 p-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            aria-hidden="true"
                                            data-slot="icon"
                                            className="h-6 w-6 text-indigo-600"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
                                            ></path>
                                        </svg>
                                    </div>
                                    <dt className="text-lg font-semibold leading-7 text-gray-900">
                                        Revenue Growth
                                    </dt>
                                </div>
                                <dd className="mt-4 flex flex-auto flex-col">
                                    <p className="flex-auto text-base leading-7 text-gray-600">
                                        Drive repeat visits with a loyalty program designed to grow
                                        your revenue over time.
                                    </p>
                                    {/*<div className="mt-4 text-sm font-medium text-indigo-600">*/}
                                    {/*    Up to 25% increase*/}
                                    {/*</div>*/}
                                </dd>
                            </div>
                            <div className="flex flex-col p-6 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-x-3">
                                    <div className="rounded-lg bg-indigo-50 p-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            aria-hidden="true"
                                            data-slot="icon"
                                            className="h-6 w-6 text-indigo-600"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z"
                                            ></path>
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z"
                                            ></path>
                                        </svg>
                                    </div>
                                    <dt className="text-lg font-semibold leading-7 text-gray-900">
                                        Customer Insights
                                    </dt>
                                </div>
                                <dd className="mt-4 flex flex-auto flex-col">
                                    <p className="flex-auto text-base leading-7 text-gray-600">
                                        Track visits, redemptions, and top customers by points, visits, or spending in real time.
                                    </p>
                                    {/*<div className="mt-4 text-sm font-medium text-indigo-600">*/}
                                    {/*    24/7 real-time data*/}
                                    {/*</div>*/}
                                </dd>
                            </div>
                            <div className="flex flex-col p-6 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-x-3">
                                    <div className="rounded-lg bg-indigo-50 p-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke-width="1.5"
                                            stroke="currentColor"
                                            aria-hidden="true"
                                            data-slot="icon"
                                            className="h-6 w-6 text-indigo-600"
                                        >
                                            <path
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                                d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                                            ></path>
                                        </svg>
                                    </div>
                                    <dt className="text-lg font-semibold leading-7 text-gray-900">
                                        Customer Support
                                    </dt>
                                </div>
                                <dd className="mt-4 flex flex-auto flex-col">
                                    <p className="flex-auto text-base leading-7 text-gray-600">
                                        Get dedicated support and expert guidance to help you
                                        maximize your loyalty program success.
                                    </p>
                                    {/* <div className="mt-4 text-sm font-medium text-indigo-600">Up
                                        to 24/7 support
                                    </div> */}
                                </dd>
                            </div>
                            <div className="flex flex-col p-6 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all duration-300">
                                <div className="flex items-center gap-x-3">
                                    <div className="rounded-lg bg-indigo-50 p-2">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            strokeWidth="1.5"
                                            stroke="currentColor"
                                            className="h-6 w-6 text-indigo-600"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M3.75 13.5 10.5 3.75v6h3.75L6.75 20.25v-6H3.75Z"
                                            />
                                        </svg>
                                    </div>
                                    <dt className="text-lg font-semibold leading-7 text-gray-900">
                                        Easy Setup
                                    </dt>
                                </div>
                                <dd className="mt-4 flex flex-auto flex-col">
                                    <p className="flex-auto text-base leading-7 text-gray-600">
                                        Launch your loyalty program in minutes. No app required, no
                                        complex setup.
                                    </p>
                                    {/* <div className="mt-4 text-sm font-medium text-indigo-600">Up
                                        to 24/7 support
                                    </div> */}
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </motion.section>

            {/*{Picture Section}*/}
            <section className="px-6 py-24 bg-[#1E0548]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[1fr_1.25fr] gap-12 items-center">
                    {/* Copy */}
                    <div>
                        <h3 className="text-2xl md:text-4xl font-bold text-white mb-4">
                            Manage Your Loyalty Program in One Simple Dashboard
                        </h3>

                        <p className="text-gray-300 mb-6 max-w-xl">
                            Track customer visits, rewards, and performance in real time. LoyalPass
                            gives you full control — without complexity.
                        </p>

                        <ul className="space-y-4 text-sm text-gray-300">
                            <li className="flex gap-3">
                                <span className="text-[#00FFD0]">✓</span>
                                View customer activity and engagement
                            </li>
                            <li className="flex gap-3">
                                <span className="text-[#00FFD0]">✓</span>
                                Create and manage rewards in seconds
                            </li>
                            <li className="flex gap-3">
                                <span className="text-[#00FFD0]">✓</span>
                                Track redemptions, visits, and top customers by points, visits, or spending
                            </li>
                            <li className="flex gap-3">
                                <span className="text-[#00FFD0]">✓</span>
                                Visibility Checker — see how your business appears online and fix gaps
                            </li>
                            <li className="flex gap-3">
                                <span className="text-[#00FFD0]">✓</span>
                                Update wallet passes instantly — no re-download needed
                            </li>
                        </ul>

                        <button className="mt-8 px-6 py-3 rounded-xl bg-[#FF4DA6] text-white font-semibold hover:opacity-90 transition">
                            See Live Demo
                        </button>
                    </div>

                    {/* Animated Screenshot */}
                    <div className="relative">
                        <div className="absolute -inset-4 bg-gradient-to-r from-[#00FFD0]/20 to-[#FF4DA6]/20 blur-2xl rounded-3xl" />

                        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={index}
                                    src={screenshots[index].src}
                                    alt={screenshots[index].alt}
                                    initial={{ opacity: 0, y: 20, scale: 0.98 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -20, scale: 0.98 }}
                                    transition={{ duration: 0.6, ease: 'easeOut' }}
                                    className="w-full"
                                />
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="px-6 py-20 bg-[#150336]">
                <div className="mx-auto max-w-2xl lg:text-center">
                    <h2 className="text-base font-semibold leading-7 text-[#FF4DA6]">
                        Built to Scale With You
                    </h2>
                    <p className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                        Powerful features for growing businesses
                    </p>
                    <p className="mt-6 text-lg leading-8 text-gray-300">
                        Scale your loyalty program with enterprise-grade features designed for
                        growing businesses.
                    </p>
                </div>
                <div className="grid">
                    <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                        <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="#00FFD0"
                                        aria-hidden="true"
                                        data-slot="icon"
                                        className="h-5 w-5 flex-none text-indigo-400"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25Z"
                                        ></path>
                                    </svg>
                                    Advanced Analytics
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300">
                                    <p className="flex-auto">
                                        Dashboard with time filters (Total, Year, Month, Week, Day), top customers by
                                        points, visits, or spending, and detailed redemption and program performance. Available on all plans with limits that scale by tier.
                                    </p>
                                </dd>
                            </div>
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="#00FFD0"
                                        aria-hidden="true"
                                        data-slot="icon"
                                        className="h-5 w-5 flex-none text-indigo-400"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                                        ></path>
                                    </svg>
                                    Visibility Checker
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300">
                                    <p className="flex-auto">
                                        See how your business appears online — website, address, Google Maps, social links.
                                        Get a presence score and fix gaps so customers can find you.
                                    </p>
                                </dd>
                            </div>
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="#00FFD0"
                                        aria-hidden="true"
                                        data-slot="icon"
                                        className="h-5 w-5 flex-none text-indigo-400"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"
                                        ></path>
                                    </svg>
                                    Custom Integration
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300">
                                    <p className="flex-auto">
                                        Seamless integration with existing Digital Wallets and
                                        business tools.
                                    </p>
                                </dd>
                            </div>
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="#00FFD0"
                                        aria-hidden="true"
                                        data-slot="icon"
                                        className="h-5 w-5 flex-none text-indigo-400"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M4.5 12a7.5 7.5 0 0 0 15 0m-15 0a7.5 7.5 0 1 1 15 0m-15 0H3m16.5 0H21m-1.5 0H12m-8.457 3.077 1.41-.513m14.095-5.13 1.41-.513M5.106 17.785l1.15-.964m11.49-9.642 1.149-.964M7.501 19.795l.75-1.3m7.5-12.99.75-1.3m-6.063 16.658.26-1.477m2.605-14.772.26-1.477m0 17.726-.26-1.477M10.698 4.614l-.26-1.477M16.5 19.794l-.75-1.299M7.5 4.205 12 12m6.894 5.785-1.149-.964M6.256 7.178l-1.15-.964m15.352 8.864-1.41-.513M4.954 9.435l-1.41-.514M12.002 12l-3.75 6.495"
                                        ></path>
                                    </svg>
                                    Flexible Configuration
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300">
                                    <p className="flex-auto">
                                        Customize every aspect of your loyalty program to match your
                                        brand and business needs.
                                    </p>
                                </dd>
                            </div>
                            <div className="flex flex-col">
                                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-white">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="#00FFD0"
                                        aria-hidden="true"
                                        data-slot="icon"
                                        className="h-5 w-5 flex-none text-indigo-400"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z"
                                        ></path>
                                    </svg>
                                    Enterprise Security
                                </dt>
                                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300">
                                    <p className="flex-auto">
                                        High-level security and compliance to protect your business
                                        and customer data.
                                    </p>
                                </dd>
                            </div>
                        </dl>
                    </div>
                </div>
            </section>

            {/* Pass Preview Section */}
            <section className="flex flex-col items-center justify-center py-20 bg-[#0D0221] text-center px-4">
                <h3 className="text-2xl md:text-4xl font-bold text-white mb-4">
                    Your Brand, Inside Your Customers’ Wallet
                </h3>

                <p className="text-md md:text-lg text-gray-300 max-w-2xl mb-6">
                    Create a fully custom digital loyalty pass that lives in Apple Wallet and Google
                    Wallet. No app downloads — just instant access to rewards, points, and visits.
                </p>

                {/* Feature bullets */}
                <div className="flex flex-wrap justify-center gap-4 mb-10 text-sm text-gray-300">
                    <span className="flex items-center gap-2">✓ Apple Wallet & Google Wallet</span>
                    <span className="flex items-center gap-2">✓ Custom branding & colors</span>
                    <span className="flex items-center gap-2">✓ QR code for fast scanning</span>
                    <span className="flex items-center gap-2">✓ Real-time reward updates</span>
                </div>

                {/* Pass Preview emphasis */}
                <div className="relative">
                    <motion.div
                        className="absolute -inset-6 rounded-3xl blur-2xl bg-gradient-to-r from-[#00FFD0]/20 to-[#FF4DA6]/20"
                        animate={{ opacity: [0.25, 0.55, 0.25] }}
                        transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    <motion.div
                        className="relative"
                        initial={{ scale: 0.95 }}
                        animate={{ scale: [1, 1.03, 1] }}
                        transition={{ duration: 2.5, repeat: Infinity }}
                    >
                        <PassPreview
                            merchantName="LoyalPass Cafe"
                            programName="Magic Rewards"
                            logoUrl="/test.png"
                            email="analuisa@email.com"
                            points={points}
                            visits={visits}
                            brandColor="#150336"
                            textColor="#FFFFFF"
                        />
                    </motion.div>
                </div>
                <br />
                <p className="text-md md:text-lg text-gray-300 max-w-xl italic">
                    “Customers keep your brand in their wallet.”
                </p>
            </section>

            {/* Pricing */}
            <section id="pricing" className="px-6 py-20 bg-[#1E0548] text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-[#00FFD0] mb-3">
                    Pricing Plans
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-4">
                    Simple plans for every stage — launch fast, grow loyalty, and scale when you’re
                    ready. No long-term contracts.
                </p>
                <p className="text-gray-500 text-xs max-w-2xl mx-auto mb-12">
                    Paid subscriptions are billed in advance. By subscribing you agree to our{' '}
                    <Link to="/terms-of-service" className="text-[#00FFD0] hover:underline">
                        Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/refund-policy" className="text-[#00FFD0] hover:underline">
                        Refund Policy
                    </Link>
                    .
                </p>
                {/* <p className="text-gray-400 text-sm max-w-2xl mx-auto mb-12">
                    All tiers include the same core features; limits (e.g. passes, rewards, emails, sms) scale by plan. Multiple reward systems, SMS campaigns, and higher support levels are available on Pro and Enterprise only.
                </p> */}

                <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto items-stretch">
                    {/* Starter */}
                    <div className="bg-[#150336] p-8 rounded-2xl shadow-lg border border-white/10 hover:border-[#FF4DA6]/60 transition flex flex-col">
                        <h3 className="text-xl font-bold text-[#FF4DA6] mb-2">Starter</h3>
                        <p className="text-sm text-gray-400 mb-6">
                            For businesses launching their first loyalty program.
                        </p>

                        {/* Optional price placeholder */}
                        {/* <p className="text-4xl font-bold text-white mb-6">$49<span className="text-base text-gray-400">/mo</span></p> */}

                        <ul className="text-sm text-gray-300 space-y-3 text-left mb-8 flex-1">
                            <li>✓ Dashboard &amp; analytics</li>
                            <li>✓ Create Custom Rewards (limits apply)</li>
                            <li>✓ Generate and Scan Wallet Passes (limits apply)</li>
                            <li>✓ Digital wallet pass (Apple + Android)</li>
                            <li>✓ Email Campaigns</li>
                            <li>✓ Visibility Checker</li>
                            <li>✓ Email support</li>
                        </ul>

                        <Link
                            to="/register"
                            className="w-full py-3 rounded-xl bg-[#00FFD0] text-[#0D0221] font-semibold hover:bg-[#00e6bc] transition mt-auto text-center block"
                        >
                            Start Free Trial
                        </Link>
                    </div>

                    {/* Pro (Most Popular) */}
                    <div className="relative bg-[#150336] p-8 rounded-2xl shadow-lg border-2 border-[#00FFD0] hover:shadow-xl transition flex flex-col">
                        <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold bg-[#00FFD0] text-[#0D0221]">
                            Most Popular
                        </div>

                        <h3 className="text-xl font-bold text-[#00FFD0] mb-2">Pro</h3>
                        <p className="text-sm text-gray-400 mb-6">
                            For growing businesses that want automation and insights.
                        </p>

                        {/* Optional price placeholder */}
                        {/* <p className="text-4xl font-bold text-white mb-6">$99<span className="text-base text-gray-400">/mo</span></p> */}

                        <ul className="text-sm text-gray-300 space-y-3 text-left mb-8 flex-1">
                            <li>✓ Everything in Starter, with higher limits</li>
                            <li>✓ Multiple reward systems</li>
                            <li>✓ Email &amp; SMS campaigns</li>
                            <li>✓ Visibility Checker with GBP insights</li>
                            <li>✓ Priority support</li>
                        </ul>

                        <button className="w-full py-3 rounded-xl bg-[#FF4DA6] text-white font-semibold hover:opacity-90 transition mt-auto">
                            Book a Demo
                        </button>
                    </div>

                    {/* Enterprise */}
                    <div className="bg-[#150336] p-8 rounded-2xl shadow-lg border border-white/10 hover:border-[#FF4DA6]/60 transition flex flex-col">
                        <h3 className="text-xl font-bold text-[#FF4DA6] mb-2">Enterprise</h3>
                        <p className="text-sm text-gray-400 mb-6">
                            Highest limits, custom integrations, and dedicated support.
                        </p>

                        {/* Optional price placeholder */}
                        {/* <p className="text-4xl font-bold text-white mb-6">Custom</p> */}

                        <ul className="text-sm text-gray-300 space-y-3 text-left mb-8 flex-1">
                            <li>✓ Everything in Pro, with custom limits</li>
                            <li>✓ Unlimited customers</li>
                            <li>✓ Custom integrations &amp; onboarding</li>
                            <li>✓ Dedicated account manager</li>
                            <li>✓ 24/7 support</li>
                        </ul>

                        <button className="w-full py-3 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/15 transition mt-auto">
                            Contact Sales
                        </button>
                    </div>
                </div>

                {/* Trust row */}
                <div className="mt-12 max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-center gap-4 text-sm text-gray-300">
                    <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">
                        No contracts
                    </span>
                    <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">
                        Cancel anytime
                    </span>
                    <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">
                        Setup help included
                    </span>
                </div>
            </section>

            {/* Contact CTA */}
            <section id="contact" className="px-6 py-20 bg-[#150336] text-center">
                <div>
                    <div className="flex items-center justify-center gap-2 mb-4">
                        <div className="flex items-center gap-1 text-sm text-white">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                                data-slot="icon"
                                className="h-4 w-4 text-yellow-400"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                                ></path>
                            </svg>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                                data-slot="icon"
                                className="h-4 w-4 text-yellow-400"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                                ></path>
                            </svg>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                                data-slot="icon"
                                className="h-4 w-4 text-yellow-400"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                                ></path>
                            </svg>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                                data-slot="icon"
                                className="h-4 w-4 text-yellow-400"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                                ></path>
                            </svg>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                aria-hidden="true"
                                data-slot="icon"
                                className="h-4 w-4 text-yellow-400"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"
                                ></path>
                            </svg>
                            {/* <span>4.9/5 from 500+ reviews</span> */}
                        </div>
                    </div>
                </div>
                <br />
                <h2 className="text-3xl font-bold text-[#00FFD0]">
                    Ready to transform your customer relationships?
                </h2>
                <p className="mt-2 text-gray-300 max-w-xl mx-auto">
                    Join successful businesses that are growing their customer base and increasing
                    revenue with our powerful loyalty program platform.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                    <Link
                        to="/register"
                        className="inline-block bg-[#00FFD0] text-[#0D0221] px-8 py-3 rounded-full font-semibold hover:bg-[#00e6bc] transition"
                    >
                        Start Free Trial
                    </Link>
                    <a
                        href="mailto:loyalpassapp@gmail.com"
                        className="inline-block border border-white/30 text-white px-8 py-3 rounded-full font-semibold hover:border-[#FF4DA6] hover:text-[#FF4DA6] transition"
                    >
                        Contact Us
                    </a>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#0D0221] text-sm text-gray-400 py-12 px-6">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 items-start">
                    {/* Brand */}
                    <div className="flex flex-col items-center md:items-start text-center md:text-left">
                        <img src="/test.png" alt="LoyalPass Logo" className="h-10 mb-3" />
                        <span className="text-white text-lg font-semibold">
                            Loyal<span className="text-[#FF4DA6]">Pass</span>
                        </span>
                        <p className="mt-2 text-sm text-gray-400 max-w-xs">
                            Digital loyalty programs that help small businesses increase repeat
                            customers and revenue.
                        </p>
                    </div>

                    {/* Product */}
                    <div className="text-center md:text-left">
                        <h4 className="text-white font-semibold mb-3">Product</h4>
                        <ul className="space-y-2">
                            <li>
                                <Link to="/register" className="hover:text-[#00FFD0]">
                                    Start Free Trial
                                </Link>
                            </li>
                            <li>
                                <a href="#pricing" className="hover:text-[#00FFD0]">
                                    Pricing
                                </a>
                            </li>
                            <li>
                                <a href="#features" className="hover:text-[#00FFD0]">
                                    Features
                                </a>
                            </li>
                            <li>
                                <a href="#demo" className="hover:text-[#00FFD0]">
                                    Book a Demo
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div className="text-center md:text-left">
                        <h4 className="text-white font-semibold mb-3">Contact</h4>
                        <ul className="space-y-2">
                            <li>
                                <a
                                    href="mailto:loyalpassapp@gmail.com"
                                    className="hover:text-[#00FFD0]"
                                >
                                    loyalpassapp@gmail.com
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Social */}
                    <div className="flex flex-col items-center md:items-end">
                        <h4 className="text-white font-semibold mb-3">Follow Us</h4>
                        <a
                            href="https://www.instagram.com/astrawebmiami"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                            className="group"
                        >
                            <svg
                                className="w-8 h-8 fill-current text-[#FF4DA6] group-hover:text-[#00FFD0] transition"
                                viewBox="0 0 24 24"
                            >
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.353 3.608 1.328.975.975 1.266 2.242 1.328 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.353 2.633-1.328 3.608-.975.975-2.242 1.266-3.608 1.328-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.353-3.608-1.328-.975-.975-1.266-2.242-1.328-3.608C2.175 15.747 2.163 15.367 2.163 12s.012-3.584.07-4.85c.062-1.366.353-2.633 1.328-3.608.975-.975 2.242-1.266 3.608-1.328C8.416 2.175 8.796 2.163 12 2.163z" />
                            </svg>
                        </a>
                    </div>
                </div>

                <div className="mt-10 border-t border-white/10 pt-6">
                    <LandingLegalFooter />
                </div>
            </footer>
        </div>
    );
}
